import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';

import ChildInvoiceLink from '../../../models/ChildInvoiceLink';
import { LinkFile } from '../../../models/LinkFile';
import { LinkedObjectToFile } from '../../../models/LinkedObjectToFile';
import ProjectAccount from '../../../models/ProjectAccount';
import { Timesheet } from '../../../models/Timesheet';
import { Timeslot } from '../../../models/Timeslot';
import YukiDocument from '../../../models/yuki/YukiDocument';
import { ProjectService } from '../../../projects/project.service';
import { FetchTimesheetsByProjectIdAndYearAndMonth, ResetTimesheetsByProjectIdAndYearAndMonth } from '../../../timesheets/timesheets.actions';
import { TimesheetState } from '../../../timesheets/timesheets.state';
import { roundNumber } from '../../utils/number-utils';
import { AddChildLinkConfirmFromTimesheetsModalComponent } from '../add-child-link-confirm-from-timesheets-modal/add-child-link-confirm-from-timesheets-modal.component';
import { AddSubLinkToLinkedObjectToFile, DeleteSubLinkToLinkedObjectToFile, GetAllObjectsToLink, UpdateChildInvoiceLink } from '../global-files.actions';
import { GlobalFilesState } from '../global-files.state';

@Component({
    selector: 'app-add-child-link-modal',
    templateUrl: './add-child-link-modal.component.html',
    styleUrls: ['./add-child-link-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class AddChildLinkModalComponent implements OnInit, OnDestroy {
    @Input() link: LinkedObjectToFile;
    @Input() file: YukiDocument;
    @Input() allowedTypes: string[] | null = null;
    @Input() defaultType: string = 'project';
    @Select(GlobalFilesState.LinkFiles) linkFiles$: Observable<LinkFile[]>;
    @Select(TimesheetState.timesheetsForProjectInMonthAndYear) timesheetsForProjectInMonthAndYear$: Observable<Timesheet[]>;
    linkFiles: LinkFile[] = [];
    shownLinkedFiles: LinkFile[] = [];
    newChildLinks: ChildInvoiceLinkDTO[] = [];
    existingLinksDatasource: MatTableDataSource<ChildInvoiceLink> = new MatTableDataSource<ChildInvoiceLinkDTO>();
    displayedColumns: string[] = ['object', 'amount', 'remove'];
    linkFileForm: UntypedFormGroup;
    amountOverflowMessage: string;
    linkFilesLoaded = false;
    timesheetsLoaded = false;
    timesheetsForProjectInMonthAndYear: Timesheet[];
    employeeWorkedMinutesForProjectMap: Map<string, number> = new Map<string, number>();
    totalHoursForProject: number;
    activeLinkType = 'project';
    projectContractEmployeeIds: Set<string> = new Set<string>();
    projectAccountEmployeeIds: Set<string> = new Set<string>();
    projectAccountIds: Set<string> = new Set<string>();
    projectPersonEmployeeIds: Set<string> = new Set<string>();
    projectAccountEmployeeByAccountId: Map<string, string> = new Map<string, string>();
    selectedProjectAccountIdForPersonFilter: string | null = null;
    projectContractLinkFiles: LinkFile[] = [];
    projectAccountLinkFiles: LinkFile[] = [];
    projectAccountOptionLinkFiles: LinkFile[] = [];
    projectPersonLinkFiles: LinkFile[] = [];

    isUpdating = false;
    isSaving = false;
    isDeleting = false;
    unsavedChanges: boolean;

    private subscriptions = new Subscription();
    // when prefill is triggered programmatically we can auto-close the parent modal
    private autoCloseAfterConfirm = false;

    constructor(
        private store: Store,
        public activeModal: NgbActiveModal,
        private fb: UntypedFormBuilder,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private projectService: ProjectService,
    ) {}

    ngOnInit(): void {
        this.createLinkFileForm();
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.store.dispatch(new ResetTimesheetsByProjectIdAndYearAndMonth());
    }

    updateLinkType(value: string) {
        const normalizedType = this.normalizeType(value || this.defaultType || 'project');
        this.activeLinkType = normalizedType;

        if (normalizedType === 'contract') {
            this.shownLinkedFiles = this.projectContractLinkFiles;
            return;
        }

        if (normalizedType === 'projectaccount') {
            this.shownLinkedFiles = this.projectAccountOptionLinkFiles;
            return;
        }

        if (normalizedType === 'employee') {
            this.shownLinkedFiles = this.getScopedPersonLinkFiles();
            return;
        }

        this.shownLinkedFiles = this.linkFiles.filter((linkFile) => this.normalizeType(linkFile.type) === normalizedType);
    }

    isTypeAllowed(type: string): boolean {
        const normalizedType = this.normalizeType(type);

        if (!this.allowedTypes || this.allowedTypes.length === 0) {
            return normalizedType === 'project' || normalizedType === 'employee' || normalizedType === 'budget' || normalizedType === 'overheadcategory';
        }

        const normalizedAllowedTypes = this.allowedTypes.map((value) => this.normalizeType(value));
        return normalizedAllowedTypes.includes(normalizedType);
    }

    isCurrentType(type: string): boolean {
        return this.activeLinkType === this.normalizeType(type);
    }

    addLinkFile(linkFile: LinkFile) {
        let targetLinkFile: LinkFile = linkFile;
        if (this.activeLinkType === 'projectaccount') {
            this.captureSelectedProjectAccountForPersonFilter(linkFile);
            const accountId = (linkFile?.uuid || '').toString();
            const employeeId = this.projectAccountEmployeeByAccountId.get(accountId);
            if (!employeeId) {
                this.toastr.warning('Selected project account has no linked person. Link an employee on the project account first.');
                return;
            }

            const employeeLinkFile = this.linkFiles.find(
                (candidate) => this.normalizeType(candidate?.type) === 'employee' && (candidate?.uuid || '').toString() === employeeId,
            );

            if (!employeeLinkFile) {
                this.toastr.warning('Could not resolve employee link for the selected project account.');
                return;
            }

            targetLinkFile = employeeLinkFile;
        }

        const effectiveType = this.getEffectiveChildLinkType(targetLinkFile);
        const newChildLink: ChildInvoiceLinkDTO = {
            objectIdForKey: this.link.objectId,
            fileIdForKey: this.file.uuid,
            newObjectLinkId: targetLinkFile.uuid,
            type: effectiveType,
            amount: this.calculateAmount(),
        };
        this.newChildLinks.push(newChildLink);
        this.saveNewChildLinks();
        this.linkFileForm.controls['object'].reset();
    }

    saveNewChildLinks() {
        this.isSaving = true;
        this.subscriptions.add(
            this.store.dispatch(new AddSubLinkToLinkedObjectToFile(this.newChildLinks)).subscribe({
                next: () => {
                    this.isSaving = false;
                    this.newChildLinks = [];
                    this.store
                        .selectOnce(GlobalFilesState.LinkedObjectsToFileWithId)
                        .subscribe(
                            (linkedObjects) =>
                                (this.existingLinksDatasource.data = linkedObjects.linkedObjects.find(
                                    (linkedObject) => linkedObject.objectId === this.link.objectId,
                                ).childInvoiceLinks),
                        );
                },
            }),
        );
    }

    deleteExistingLink(row) {
        this.isDeleting = true;
        this.subscriptions.add(
            this.store.dispatch(new DeleteSubLinkToLinkedObjectToFile(row.id)).subscribe({
                next: (value) => {
                    this.isDeleting = false;
                    this.existingLinksDatasource.data = value.globalFiles.linkedObjectsToFile.linkedObjects.find(
                        (linkedObject) => linkedObject.objectId === this.link.objectId,
                    ).childInvoiceLinks;
                },
            }),
        );
    }

    updateLinkAmount(row: ChildInvoiceLink, newPrice: number) {
        this.isUpdating = true;
        const childInvoiceLinkDTO: ChildInvoiceLinkDTO = {
            amount: newPrice,
        };
        this.subscriptions.add(
            this.store.dispatch(new UpdateChildInvoiceLink(row.id, childInvoiceLinkDTO)).subscribe({
                next: () => {
                    this.isUpdating = false;
                    this.unsavedChanges = false;
                },
            }),
        );
    }

    inputEvent() {
        this.unsavedChanges = true;
    }

    checkAmountOverflow(): void {
        if (this.calculateLinkedAmount() > this.link.price) {
            this.amountOverflowMessage = `Linked amount is over total amount of parent by `;
        } else if (this.calculateLinkedAmount() < this.link.price) {
            this.amountOverflowMessage = `Linked amount is under total amount of parent by `;
        } else {
            this.amountOverflowMessage = null;
        }
    }

    private fetchData() {
        this.existingLinksDatasource.data = this.link.childInvoiceLinks;
        this.subscriptions.add(
            this.linkFiles$.subscribe((linkFiles) => {
                if (!linkFiles) {
                    this.store.dispatch(new GetAllObjectsToLink());
                    return;
                }
                this.linkFilesLoaded = true;
                this.linkFiles = linkFiles;
                if (this.allowedTypes && this.allowedTypes.length > 0) {
                    this.linkFiles = this.linkFiles.filter((linkFile) => this.isLinkFileAllowedByConfiguredTypes(linkFile));
                }

                this.loadProjectScopedOptionsAndApplyDefaults();

                if (this.timesheetsLoaded) this.calculateDefaultSublinkPercentages();
            }),
        );

        if (this.link.objectType.toLowerCase() !== 'project' || this.file.folderId !== 2 || !this.fetchingTimesheetsIsNecessary()) return;

        this.subscriptions.add(
            this.timesheetsForProjectInMonthAndYear$.subscribe((timesheets) => {
                if (!timesheets) {
                    const documentDate: Date = new Date(this.file.documentDate);
                    this.store.dispatch(
                        new FetchTimesheetsByProjectIdAndYearAndMonth(this.link.objectId, documentDate.getFullYear(), documentDate.getMonth() + 1),
                    );

                    return;
                }

                this.timesheetsLoaded = true;
                this.timesheetsForProjectInMonthAndYear = timesheets;

                if (this.linkFilesLoaded) this.calculateDefaultSublinkPercentages();
            }),
        );
    }

    private normalizeType(type: string): string {
        return (type || '').toLowerCase();
    }

    private isLinkFileAllowedByConfiguredTypes(linkFile: LinkFile): boolean {
        if (!this.allowedTypes || this.allowedTypes.length === 0) {
            return true;
        }

        const normalizedAllowedTypes = this.allowedTypes.map((value) => this.normalizeType(value));
        const linkFileType = this.normalizeType(linkFile?.type);

        if (normalizedAllowedTypes.includes(linkFileType)) {
            return true;
        }

        if (linkFileType === 'employee') {
            return normalizedAllowedTypes.includes('contract') || normalizedAllowedTypes.includes('projectaccount');
        }

        return false;
    }

    private getEffectiveChildLinkType(linkFile: LinkFile): string {
        if (this.activeLinkType === 'contract') {
            return 'employee';
        }

        if (this.activeLinkType === 'projectaccount') {
            return 'employee';
        }

        return linkFile?.type || this.activeLinkType;
    }

    private getAllowedTypesInOrder(): string[] {
        const configuredTypes = (this.allowedTypes || []).map((type) => this.normalizeType(type));
        if (configuredTypes.length > 0) {
            return configuredTypes;
        }

        return ['project', 'employee', 'budget', 'overheadcategory'];
    }

    private getFilesByType(type: string): LinkFile[] {
        const normalizedType = this.normalizeType(type);

        if (normalizedType === 'contract') {
            return this.projectContractLinkFiles;
        }

        if (normalizedType === 'projectaccount') {
            return this.projectAccountOptionLinkFiles;
        }

        if (normalizedType === 'employee') {
            return this.projectPersonLinkFiles;
        }

        return this.linkFiles.filter((linkFile) => this.normalizeType(linkFile.type) === normalizedType);
    }

    private async loadProjectScopedOptionsAndApplyDefaults(): Promise<void> {
        await this.loadProjectScopedEmployeeFilters();

        const normalizedDefaultType = this.normalizeType(this.defaultType || 'project');
        const orderedTypes = [normalizedDefaultType, ...this.getAllowedTypesInOrder()];
        const effectiveType = orderedTypes.find((type) => this.getFilesByType(type).length > 0) || normalizedDefaultType;

        this.updateLinkType(effectiveType);
    }

    private async loadProjectScopedEmployeeFilters(): Promise<void> {
        this.projectContractEmployeeIds = new Set<string>();
        this.projectAccountEmployeeIds = new Set<string>();
        this.projectAccountIds = new Set<string>();
        this.projectPersonEmployeeIds = new Set<string>();
        this.projectAccountEmployeeByAccountId = new Map<string, string>();
        this.projectContractLinkFiles = [];
        this.projectAccountLinkFiles = [];
        this.projectAccountOptionLinkFiles = [];
        this.projectPersonLinkFiles = [];
        this.selectedProjectAccountIdForPersonFilter = null;

        if (this.link?.objectType?.toLowerCase() !== 'project' || !this.link?.objectId) {
            return;
        }

        const needsContractList = this.isTypeAllowed('contract');
        const needsProjectAccountList = this.isTypeAllowed('projectAccount');
        const needsPersonList = this.isTypeAllowed('employee');
        if (!needsContractList && !needsProjectAccountList && !needsPersonList) {
            return;
        }

        try {
            const [project, projectAccounts] = await Promise.all([
                this.projectService.getProjectDetail(this.link.objectId).toPromise(),
                this.projectService.getProjectAccounts(this.link.objectId).toPromise(),
            ]);

            (project?.defaultLinkedEmployees || []).forEach((defaultLink) => {
                if (defaultLink?.employeeId) {
                    this.projectContractEmployeeIds.add(defaultLink.employeeId);
                    this.projectPersonEmployeeIds.add(defaultLink.employeeId);
                }
            });

            (projectAccounts || []).forEach((account: ProjectAccount) => {
                if (account?.uuid) {
                    this.projectAccountIds.add(account.uuid);
                    if (account?.employeeId) {
                        this.projectAccountEmployeeByAccountId.set(account.uuid, account.employeeId);
                    }
                }
                if (account?.employeeId) {
                    this.projectAccountEmployeeIds.add(account.employeeId);
                    this.projectPersonEmployeeIds.add(account.employeeId);
                }
            });

            this.projectAccountOptionLinkFiles = (projectAccounts || [])
                .filter((account: ProjectAccount) => !!account?.uuid)
                .map((account: ProjectAccount) => {
                    const accountCode = (account?.accountCode || account?.jiraAccountId || '').toString().trim();
                    const accountTitle = (account?.accountTitle || account?.accountName || '').toString().trim();
                    const displayName =
                        accountCode && accountTitle && accountTitle !== accountCode
                            ? `${accountCode} (${accountTitle})`
                            : accountCode || accountTitle || 'Project account';

                    return {
                        uuid: account.uuid,
                        displayName,
                        type: 'projectaccount',
                        budgetArchived: false,
                        budgetStartDate: null,
                        budgetEndDate: null,
                    } as LinkFile;
                });
        } catch (error) {
            this.toastr.warning('Project contract/account data could not be loaded.');
        }

        const employeeLinkFiles = this.linkFiles.filter((linkFile) => this.normalizeType(linkFile.type) === 'employee');
        this.projectContractLinkFiles = employeeLinkFiles.filter((employeeLinkFile) => this.projectContractEmployeeIds.has(employeeLinkFile.uuid));
        this.projectAccountLinkFiles = employeeLinkFiles.filter((employeeLinkFile) => this.projectAccountEmployeeIds.has(employeeLinkFile.uuid));

        this.projectPersonLinkFiles = employeeLinkFiles.filter((employeeLinkFile) => this.projectPersonEmployeeIds.has(employeeLinkFile.uuid));
        if (!this.projectPersonLinkFiles.length) {
            this.projectPersonLinkFiles = employeeLinkFiles;
        }
    }

    private getScopedPersonLinkFiles(): LinkFile[] {
        const selectedAccountId = (this.selectedProjectAccountIdForPersonFilter || '').toString();
        if (!selectedAccountId) {
            return this.projectPersonLinkFiles;
        }

        const scopedEmployeeId = this.projectAccountEmployeeByAccountId.get(selectedAccountId);
        if (!scopedEmployeeId) {
            return this.projectPersonLinkFiles;
        }

        const scopedEmployees = this.projectPersonLinkFiles.filter((employeeLinkFile) => employeeLinkFile?.uuid === scopedEmployeeId);
        return scopedEmployees.length > 0 ? scopedEmployees : this.projectPersonLinkFiles;
    }

    private captureSelectedProjectAccountForPersonFilter(linkFile: LinkFile): void {
        const normalizedLinkType = this.normalizeType(linkFile?.type);
        if (normalizedLinkType === 'projectaccount') {
            this.selectedProjectAccountIdForPersonFilter = linkFile?.uuid || null;
            return;
        }

        if (normalizedLinkType !== 'employee') {
            this.selectedProjectAccountIdForPersonFilter = null;
            return;
        }

        const matchingAccountIds = Array.from(this.projectAccountEmployeeByAccountId.entries())
            .filter(([, employeeId]) => employeeId === linkFile?.uuid)
            .map(([accountId]) => accountId);

        this.selectedProjectAccountIdForPersonFilter = matchingAccountIds.length === 1 ? matchingAccountIds[0] : null;
    }

    /**
     * Prefill this modal when opened programmatically.
     * Sets the link and file, creates the form and triggers data fetching so
     * timesheet prefill logic can run and open the confirm dialog when applicable.
     */
    public async prefillFromProjectLink(link: LinkedObjectToFile, file: YukiDocument, autoCloseAfterConfirm: boolean = true): Promise<void> {
        try {
            if (!link || !file) return;
            this.link = link;
            this.file = file;
            // recreate form (ensures amountExcl and related fields are in sync)
            this.createLinkFileForm();
            // set existing links datasource so UI shows current child links immediately
            this.existingLinksDatasource.data = this.link?.childInvoiceLinks || [];
            // set autClose flag for parent closing after confirm
            this.autoCloseAfterConfirm = !!autoCloseAfterConfirm;
            // trigger fetching linkFiles and timesheets; calculateDefaultSublinkPercentages will run when data arrives
            this.fetchData();
        } catch (e) {
            // don't break the caller flow if prefill fails
            console.debug('prefillFromProjectLink failed', e);
        }
    }

    private fetchingTimesheetsIsNecessary(): boolean {
        return !this.link.childInvoiceLinks || this.link.childInvoiceLinks?.length === 0;
    }

    private calculateDefaultSublinkPercentages() {
        this.totalHoursForProject = this.timesheetsForProjectInMonthAndYear.reduce(
            (total: number, timesheet: Timesheet) =>
                total +
                timesheet.timeslots.reduce((total: number, timeslot: Timeslot) => {
                    if (timeslot.project.uuid === this.link.objectId) {
                        this.employeeWorkedMinutesForProjectMap.set(timesheet.employee.uuid, timeslot.minutes);
                        return total + timeslot.minutes;
                    }

                    return total;
                }, 0),
            0,
        );

        const newLinksFromTimesheets: ChildInvoiceLinkDTO[] = [];
        this.timesheetsForProjectInMonthAndYear.forEach((timesheet) => {
            const minutes = this.employeeWorkedMinutesForProjectMap.get(timesheet.employee.uuid) || 0;
            const amount = this.totalHoursForProject > 0 ? roundNumber((minutes / this.totalHoursForProject) * this.link.price) : 0;
            const newChildLink: ChildInvoiceLinkDTO = {
                objectIdForKey: this.link.objectId,
                fileIdForKey: this.file.uuid,
                newObjectLinkId: timesheet.employee.uuid,
                amount: amount,
                type: 'employee',
            };
            newLinksFromTimesheets.push(newChildLink);
        });

        // Only keep links with a positive amount
        const usableNewLinks = newLinksFromTimesheets.filter((nl) => !!nl && typeof nl.amount === 'number' && nl.amount > 0);

        if (this.totalHoursForProject <= 0 || usableNewLinks.length === 0) {
            // If this was triggered programmatically and the caller expects the parent modal to close,
            // close it so the automated flow can continue to the next item.
            if (this.autoCloseAfterConfirm) {
                try {
                    this.activeModal.close();
                } catch (e) {
                    // ignore
                }
            } else {
                try {
                    this.toastr.info('No timesheets found for this project/month — skipping sub links.');
                } catch (e) {
                    // ignore
                }
            }

            return;
        }

        const modalRef = this.modalService.open(AddChildLinkConfirmFromTimesheetsModalComponent, {
            windowClass: 'modal-prompt modal-prompt--min-content',
            animation: false,
        });
        modalRef.componentInstance.newChildLinks = usableNewLinks;
        modalRef.componentInstance.employeeWorkedMinutesForProjectMap = this.employeeWorkedMinutesForProjectMap;
        modalRef.componentInstance.totalHoursForProject = this.totalHoursForProject;
        modalRef.componentInstance.linkPrice = this.link.price;
        modalRef.componentInstance.alreadyLinkedPrice = this.link.childInvoiceLinks?.reduce(
            (previousValue: number, currentValue: ChildInvoiceLinkDTO) => previousValue + currentValue.amount,
            0,
        );

        // If this modal was opened programmatically and prefill requested auto-confirm,
        // trigger the confirm modal's saveLinks() immediately so the flow proceeds headless.
        if (this.autoCloseAfterConfirm && modalRef.componentInstance && typeof modalRef.componentInstance.saveLinks === 'function') {
            try {
                modalRef.componentInstance.saveLinks();
            } catch (e) {
                console.debug('Auto saveLinks failed', e);
            }
        }

        modalRef.result.then(
            () => {
                this.store
                    .selectOnce(GlobalFilesState.LinkedObjectsToFileWithId)
                    .subscribe(
                        (linkedObjects) =>
                            (this.existingLinksDatasource.data = linkedObjects.linkedObjects.find(
                                (linkedObject) => linkedObject.objectId === this.link.objectId,
                            )?.childInvoiceLinks),
                    );
                // if this modal was prefilling programmatically, close the parent modal after confirmation
                if (this.autoCloseAfterConfirm) {
                    try {
                        this.autoCloseAfterConfirm = false;
                        this.activeModal.close();
                    } catch (e) {
                        // ignore
                    }
                }
            },
            () => {
                // dismissed - nothing to do, continue the flow
            },
        );
    }

    private createLinkFileForm() {
        this.linkFileForm = this.fb.group({
            object: [null, Validators.required],
        });
    }

    private calculateLinkedAmount(): number {
        const totalAssignedAmount =
            this.existingLinksDatasource.data?.reduce((previousValue: number, currentValue: ChildInvoiceLinkDTO) => previousValue + currentValue.amount, 0) +
            this.newChildLinks.reduce((previousValue: number, currentValue: ChildInvoiceLinkDTO) => previousValue + currentValue.amount, 0);
        return roundNumber(totalAssignedAmount);
    }

    private calculateAmount(): number {
        return roundNumber(this.link.price - this.calculateLinkedAmount());
    }
}

export class ChildInvoiceLinkDTO {
    objectIdForKey?: string;
    fileIdForKey?: string;
    newObjectLinkId?: string;
    amount?: number;
    type?: string;
}
